import { format, parseISO } from "date-fns";
import { FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export interface GroupedListInvoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  created_at: string;
}

interface InvoiceGroupedListProps<T extends GroupedListInvoice> {
  invoices: T[];
  emptyMessage?: string;
  onInvoiceClick?: (invoice: T) => void;
  maxHeight?: string;
  showStatus?: boolean;
}

const statusLabel: Record<string, string> = {
  pending: "Pending",
  early_payment_scheduled: "Scheduled",
  early_payment_completed: "Completed",
  standard_payment: "Standard",
  early_payment: "Scheduled",
  paid: "Paid",
};

const statusColor: Record<string, string> = {
  pending: "bg-warning/10 text-warning border-warning/20",
  early_payment_scheduled: "bg-success/10 text-success border-success/20",
  early_payment_completed: "bg-primary/10 text-primary border-primary/20",
  standard_payment: "bg-muted text-muted-foreground border-border",
  early_payment: "bg-success/10 text-success border-success/20",
  paid: "bg-primary/10 text-primary border-primary/20",
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const formatDate = (dateString: string) => {
  try {
    return format(parseISO(dateString), "MMM d, yyyy");
  } catch {
    return dateString;
  }
};

const getMonthKey = (dateString: string) => {
  try {
    return format(parseISO(dateString), "yyyy-MM");
  } catch {
    return "unknown";
  }
};

const getMonthLabel = (monthKey: string) => {
  try {
    const [year, month] = monthKey.split("-");
    return format(new Date(parseInt(year), parseInt(month) - 1, 1), "MMMM yyyy");
  } catch {
    return monthKey;
  }
};

function computeMetrics(invoices: GroupedListInvoice[]) {
  const totalOriginal = invoices.reduce((s, i) => s + i.payment_amount, 0);
  const totalPaid = invoices.reduce(
    (s, i) => s + (i.discounted_payment_amount || i.payment_amount),
    0
  );
  const totalSavings = invoices.reduce((s, i) => s + (i.discount_amount || 0), 0);
  return { totalOriginal, totalPaid, totalSavings };
}

export function InvoiceGroupedList<T extends GroupedListInvoice>({
  invoices,
  emptyMessage = "No invoices",
  onInvoiceClick,
  maxHeight = "400px",
  showStatus = true,
}: InvoiceGroupedListProps<T>) {
  if (invoices.length === 0) {
    return <p className="text-center text-muted-foreground py-8">{emptyMessage}</p>;
  }

  // Group by month
  const groups: Record<string, T[]> = {};
  invoices.forEach((inv) => {
    const key = getMonthKey(inv.created_at);
    if (!groups[key]) groups[key] = [];
    groups[key].push(inv);
  });
  const sortedGroups = Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));

  // Overall metrics
  const overall = computeMetrics(invoices);

  return (
    <div>
      {/* Summary metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2.5 bg-muted rounded-lg">
          <p className="text-sm font-bold text-foreground font-display">
            {formatCurrency(overall.totalOriginal)}
          </p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
            Original Amount
          </p>
        </div>
        <div className="text-center p-2.5 bg-muted rounded-lg">
          <p className="text-sm font-bold text-foreground font-display">
            {formatCurrency(overall.totalPaid)}
          </p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
            Total Paid
          </p>
        </div>
        <div className="text-center p-2.5 bg-success/10 rounded-lg">
          <p className="text-sm font-bold text-success font-display">
            {formatCurrency(overall.totalSavings)}
          </p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
            Total Savings
          </p>
        </div>
      </div>

      {/* Grouped invoice list */}
      <div className="space-y-4 overflow-y-auto" style={{ maxHeight }}>
        {sortedGroups.map(([monthKey, monthInvoices]) => {
          const monthMetrics = computeMetrics(monthInvoices);
          return (
            <div key={monthKey}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <div className="h-px flex-1 bg-border" />
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {getMonthLabel(monthKey)}
                </p>
                <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                  {formatCurrency(monthMetrics.totalOriginal)}
                  {monthMetrics.totalSavings > 0 && (
                    <span className="text-success ml-1">
                      (-{formatCurrency(monthMetrics.totalSavings)})
                    </span>
                  )}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="space-y-1.5">
                {monthInvoices.map((inv) => (
                  <div
                    key={inv.id}
                    className={`flex items-center gap-2.5 p-2.5 bg-muted rounded-lg transition-colors ${onInvoiceClick ? "cursor-pointer hover:bg-muted/70" : ""}`}
                    onClick={() => onInvoiceClick?.(inv)}
                  >
                    <FileText className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">
                        {inv.supplier_name}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatDate(inv.created_at)} • Due {formatDate(inv.payment_due_date)}
                      </p>
                    </div>
                    {showStatus && (
                      <Badge
                        className={`${statusColor[inv.status] || ""} border text-[10px] px-1.5 py-0 flex-shrink-0`}
                      >
                        {statusLabel[inv.status] || inv.status}
                      </Badge>
                    )}
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-semibold text-foreground">
                        {formatCurrency(inv.payment_amount)}
                      </p>
                      {inv.discount_amount ? (
                        <p className="text-[10px] text-success">
                          -{formatCurrency(inv.discount_amount)}
                        </p>
                      ) : (
                        inv.status === "standard_payment" && (
                          <p className="text-[10px] text-muted-foreground">Full price</p>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
